'use client';

/**
 * GooeyPopover — popover qui « sort » d'un bouton avec fusion organique (smin,
 * iquilezles.org/articles/smin). Généralisation du pattern VoiceFill — technique
 * complète documentée dans la mémoire reference_gooey_smin_ui_animation.
 *
 * - Matière en SVG PUR (<g filter> + rect animé rAF) : le seul rendu fiable sur
 *   iOS/WebKit (filter: url() sur HTML n'y est pas rendu).
 * - createPortal(document.body) : échappe aux backdrop-filter/transform ancêtres.
 * - Clone du bouton au-dessus du backdrop : re-clic = fermer.
 * - Fermeture animée : quand `open` retombe, le panneau se résorbe dans le
 *   bouton (phase `closing`) avant démontage — aucun changement d'API parent.
 *
 * UNE SEULE COUCHE DE MATIÈRE. Le SVG filtré EST le fond ; le panneau HTML posé
 * dessus n'a ni fond ni ombre propres — c'est son ombre portée, aux angles moins
 * arrondis que la goutte, qui se lisait comme « un carré transparent sous le
 * contenu ». L'ombre est désormais dans le filtre, portée par la matière.
 */

import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { placePanel } from '@/lib/popover-place';

type Box = { x: number; y: number; w: number; h: number; rx: number };

type Geom = {
  W: number; H: number;
  bx: number; by: number;                                  // centre de l'ancre
  /** Géométrie RÉELLE du bouton d'ancrage — le clone et la goutte de départ
   *  l'épousent. Codées en dur (48×44, rond), elles trahissaient un deuxième
   *  bouton d'une autre forme au-dessus du vrai. */
  anchor: Box;
  panel: { x: number; y: number; w: number; h: number };   // panneau final
};

/**
 * DEUX TEMPS, jamais simultanés. À l'ouverture : le panneau sort du bouton, PUIS
 * les filtres sortent du panneau. À la fermeture, l'inverse exact : les filtres
 * rentrent dans le panneau, PUIS le panneau rentre dans le bouton. Deux gestes
 * qui se chevauchaient se lisaient comme un seul, flou.
 */
const OPEN_PANEL = 360, OPEN_FOOT = 240;
const CLOSE_FOOT = 260, CLOSE_PANEL = 420;
/**
 * Le panneau change de taille SANS se refermer : un popover à deux temps (choisir
 * puis remplir) doit s'ouvrir petit sur le choix, puis grandir. Rejouer
 * l'ouverture depuis le bouton donnerait un clignotement à chaque étape — et
 * garder une hauteur fixe taillée pour l'étape la plus longue donnerait ce grand
 * rectangle vide sous deux boutons.
 */
const GROW = 300;
/**
 * Écart entre les BORDS DE MATIÈRE, goutte à goutte. C'est lui, et lui seul, qui
 * règle l'épaisseur du filet de fusion : avec un flou d'écart-type σ = 6, le seuil
 * alpha n'est plus atteint au milieu au-delà d'environ 2,8·σ/2 ≈ 11 px. À 8 px le
 * filet est un bourrelet et les deux formes paraissent soudées ; à 11 px il ne
 * reste RIEN — le seuil tombe pile, et le lien organique disparaît juste au
 * moment où on voulait le voir.
 *
 * 9 px : le filet existe, fin, sans que les deux formes se touchent. C'est la
 * fourchette utile — un pixel de plus et il casse, un pixel de moins et il
 * s'épaissit en soudure.
 */
const GOO_GAP = 9;
/** La goutte déborde de 2 px sur le panneau et sur chaque filtre : la matière tient
 *  le contenu jusque dans les angles. Ce débord mange l'écart des deux côtés, d'où
 *  la mise en page plus large que l'écart de matière voulu. */
const BLEED = 2;
const GAP = GOO_GAP + 2 * BLEED;
/** Le pied, lui, ne déborde pas : sa matière épouse exactement la puce. On gagne
 *  ainsi 2 px d'écart de matière côté panneau — et 4 px entre deux filtres voisins —
 *  sans déplacer quoi que ce soit à l'écran, et les angles cessent de s'évaser. */
const FOOT_BLEED = 0;
/** Le seuil alpha ronge environ 5 px d'angle. Les gouttes du pied sont donc
 *  dessinées d'autant moins arrondies : sur des puces de 28 px de haut, l'arrondi
 *  naturel du filtre les transformait en pastilles. Le panneau, lui, garde son
 *  rayon tel quel — c'est son allure voulue. */
const FOOT_SQUARE = 5;
/**
 * Le MÊME rongement, vu de l'autre côté : la matière rendue est plus arrondie
 * que le rectangle dessiné. Le contenu HTML, lui, était rogné au rayon nominal —
 * ses quatre angles dépassaient donc de la goutte de ces 5 px, et l'on voyait
 * quatre petits coins durs et sans ombre, comme si la fenêtre était « coincée
 * dans quelque chose ». Le conteneur du contenu s'arrondit d'autant.
 */
const GOO_ROUND = 5;
/** Nombre maximal de gouttes de pied. Au-delà, un pied de popover n'est plus un
 *  pied : c'est un deuxième panneau. */
const FOOT_MAX = 4;
/** easeInOutCubic — départ et arrivée également amortis, dans les deux sens. */
const ease = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

const setBox = (el: SVGRectElement, b: Box) => {
  el.setAttribute('x', String(b.x));
  el.setAttribute('y', String(b.y));
  el.setAttribute('width', String(Math.max(0, b.w)));
  el.setAttribute('height', String(Math.max(0, b.h)));
  el.setAttribute('rx', String(b.rx));
};
const lerpBox = (a: Box, b: Box, e: number): Box => ({
  x: a.x + (b.x - a.x) * e, y: a.y + (b.y - a.y) * e,
  w: a.w + (b.w - a.w) * e, h: a.h + (b.h - a.h) * e,
  rx: a.rx + (b.rx - a.rx) * e,
});

export default function GooeyPopover({
  open,
  onClose,
  anchorEl,
  width = 336,
  maxHeight = 430,
  vhCap = 0.58,
  topInset,
  color = 'var(--goo-surface)',
  placement = 'above',
  align = 'center',
  backdrop = true,
  panelRadius = 24,
  cloneColor,
  footer,
  cloneIcon,
  children,
}: {
  open: boolean;
  onClose: () => void;
  anchorEl: HTMLElement | null;
  width?: number;
  maxHeight?: number;      // px — le panneau prend min(vhCap·vh, maxHeight)
  vhCap?: number;          // part max du viewport en hauteur (chat plein écran → 0.8)
  /** Marge haute réservée : ce qui flotte au-dessus (bascule jour/nuit) et sous
   *  quoi le panneau ne doit jamais passer. Par défaut 8 px, comme les bords. */
  topInset?: number;
  color?: string;
  /** Côté PRÉFÉRÉ de dépliage. Il cède automatiquement à l'autre quand la place
   *  manque — un bouton posé en haut de l'écran n'a pas de quoi s'ouvrir vers
   *  le haut, et la hauteur se recale sur ce qui reste. */
  placement?: 'above' | 'below' | 'side';
  /** De quel côté le panneau se déploie horizontalement. `center` convient à un
   *  bouton centré ; un bouton posé sur le COIN d'un encart doit, lui, déplier
   *  son panneau vers l'intérieur de l'écran — sinon il recouvre l'encart qu'on
   *  vient de quitter des yeux. `end` aligne les bords droits, `start` les
   *  gauches. Le recadrage viewport s'applique ensuite dans tous les cas. */
  align?: 'center' | 'start' | 'end';
  /** Voile sombre plein écran. Le désactiver permet de ne flouter qu'une couche
   *  choisie (la carte, et pas les feuillets) depuis la page appelante. */
  backdrop?: boolean;
  /** Arrondi du panneau. Bas = plus carré, donc plus sobre. */
  panelRadius?: number;
  /** Fond du clone, quand le bouton change de couleur à l'ouverture. Par défaut il
   *  reprend `color` : le clone EST la matière, il ne doit pas s'en détacher. */
  cloneColor?: string;
  /** Rendu SOUS le panneau, hors de sa matière (filtres, actions). Chaque enfant
   *  marqué `data-goo` reçoit SA propre goutte, taillée sur lui : deux filtres
   *  côte à côte font deux pastilles, pas une barre pleine largeur. */
  footer?: ReactNode;
  cloneIcon: ReactNode;    // icône du clone de bouton (re-clic = fermer)
  children: ReactNode;
}) {
  const rectRef = useRef<SVGRectElement | null>(null);
  const footRefs = useRef<(SVGRectElement | null)[]>([]);
  const footBoxRef = useRef<HTMLDivElement | null>(null);
  const [geom, setGeom] = useState<Geom | null>(null);
  /** Id de filtre PROPRE à chaque instance. Tant qu'il était constant
   *  (`og-goo-pop`), deux popovers montés en même temps — un panneau qui se
   *  referme (420 ms d'animation) pendant qu'un autre s'ouvre, ou deux
   *  popovers sur la même page — partageaient le PREMIER <filter> du DOM :
   *  `url(#…)` y renvoie toujours. Sa zone de travail, calculée pour SON
   *  panneau, rognait alors l'ombre de l'autre — nette d'un côté, entière de
   *  l'autre, « au petit bonheur la chance » selon l'ordre de montage. C'est
   *  le « shadowbox coupée » signalé le 10/08, jamais vraiment éteint tant
   *  que l'id restait partagé. */
  const filterId = `og-goo-pop-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  /** Dernière forme réellement peinte. C'est d'ELLE que part une croissance —
   *  repartir du bouton rejouerait l'ouverture à chaque changement d'étape. */
  const curRef = useRef<Box | null>(null);
  // Machine locale : le popover survit à open=false le temps de la résorption
  const [st, setSt] = useState<{ shown: boolean; closing: boolean }>({ shown: false, closing: false });
  if (open && !st.shown) setSt({ shown: true, closing: false });
  if (!open && st.shown && !st.closing) setSt({ shown: true, closing: true });

  const hasFoot = !!footer;
  useEffect(() => {
    if (!st.closing) return;
    const t = setTimeout(() => { curRef.current = null; setSt({ shown: false, closing: false }); },
      hasFoot ? CLOSE_FOOT + CLOSE_PANEL : CLOSE_PANEL);
    return () => clearTimeout(t);
  }, [st.closing, hasFoot]);

  useLayoutEffect(() => {
    if (!st.shown || !anchorEl) return;
    const place = () => {
      const r = anchorEl.getBoundingClientRect();
      const W = window.innerWidth, H = window.innerHeight;
      const bx = r.left + r.width / 2;
      const by = r.top + r.height / 2;
      // `placement` n'est qu'une PRÉFÉRENCE : elle cède quand le côté demandé
      // n'a pas la place. Un bouton posé sur le coin haut d'un encart s'ouvre
      // ainsi vers le haut en portrait (l'encart est en bas) et vers le bas en
      // paysage (l'encart monte jusqu'en haut) — sans que l'appelant ait à
      // connaître l'orientation. Décision isolée dans lib/popover-place.
      const { x, y, h } = placePanel({
        anchor: { top: r.top, bottom: r.bottom, left: r.left, right: r.right },
        W, H, width, maxHeight, vhCap, placement, align, gap: GAP, topInset,
      });
      // Rayon lu sur le bouton lui-même : rond, arrondi ou carré, le clone suit.
      const rx = parseFloat(getComputedStyle(anchorEl).borderTopLeftRadius) || 0;
      const next: Geom = {
        W, H, bx, by,
        anchor: { x: r.left, y: r.top, w: r.width, h: r.height, rx: Math.min(rx, r.height / 2) },
        panel: { x, y, w: width, h },
      };
      // Ne remplacer que si la géométrie a VRAIMENT changé : on replace plusieurs
      // fois par rotation (cf. plus bas), et un objet neuf à chaque passe
      // relancerait l'animation de croissance trois fois pour rien.
      setGeom(g => (g && JSON.stringify(g) === JSON.stringify(next) ? g : next));
    };
    /**
     * Une rotation d'écran ne se mesure pas au moment où elle est annoncée.
     * iOS — et Android sous barre d'URL rétractable — émettent `resize` /
     * `orientationchange` AVANT d'avoir recalculé la mise en page : `innerHeight`
     * et le rectangle du bouton valent encore ceux de l'orientation précédente.
     * Le panneau était alors taillé pour un écran haut sur un écran bas, et
     * pendait hors champ — on n'en voyait plus que le haut du bandeau.
     *
     * On replace donc plusieurs fois : tout de suite (cas desktop, instantané),
     * puis à 60 / 250 / 600 ms, le temps que le viewport se stabilise. Un
     * ResizeObserver sur la racine attrape en plus les changements de mise en
     * page qui n'émettent aucun événement de fenêtre.
     */
    let timers: ReturnType<typeof setTimeout>[] = [];
    const replace = () => {
      place();
      timers.forEach(clearTimeout);
      timers = [60, 250, 600].map(d => setTimeout(place, d));
    };
    place();
    window.addEventListener('resize', replace);
    window.addEventListener('orientationchange', replace);
    window.visualViewport?.addEventListener('resize', place);
    const ro = new ResizeObserver(place);
    ro.observe(document.documentElement);
    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener('resize', replace);
      window.removeEventListener('orientationchange', replace);
      window.visualViewport?.removeEventListener('resize', place);
      ro.disconnect();
    };
  }, [st.shown, anchorEl, width, maxHeight, vhCap, placement, align, topInset]);

  // Décollage/résorption organique : rect animé entre le bouton et le panneau
  useEffect(() => {
    if (!st.shown || !geom) return;
    const el = rectRef.current;
    if (!el) return;
    const btn = geom.anchor;
    const panel: Box = {
      x: geom.panel.x - BLEED, y: geom.panel.y - BLEED,
      w: geom.panel.w + 2 * BLEED, h: geom.panel.h + 2 * BLEED, rx: panelRadius,
    };
    // Première apparition : la matière sort du bouton. Ensuite (changement
    // d'étape, rotation), elle part de là où elle est et se contente de grandir.
    const first = !curRef.current;
    const [pFrom, pTo] = st.closing ? [panel, btn] : [first ? btn : curRef.current!, panel];
    const seed = (b: Box): Box => ({ x: b.x + b.w / 2 - 5, y: geom.panel.y + geom.panel.h - 6, w: 10, h: 6, rx: 3 });
    // Une goutte par élément `data-goo` du pied, taillée sur lui : deux filtres côte
    // à côte font deux pastilles. L'unique rect d'avant, pris sur le conteneur,
    // donnait une barre pleine largeur qui débordait des filtres.
    //
    // Mesure en offsetLeft/offsetTop, PAS en getBoundingClientRect : le pied porte
    // une animation d'apparition qui le décale de 8 px vers le bas pendant tout son
    // délai. Le rect mesuré à l'écran héritait de ce décalage — d'où des cadres
    // posés trop bas, dont le contenu débordait par le haut.
    const host = footBoxRef.current;
    const els = host ? [...host.querySelectorAll<HTMLElement>('[data-goo]')].slice(0, FOOT_MAX) : [];
    const boxes: Box[] = (els.length ? els : host ? [host] : []).map(el => ({
      x: geom.panel.x + el.offsetLeft - FOOT_BLEED,
      y: geom.panel.y + geom.panel.h + GAP + el.offsetTop - FOOT_BLEED,
      w: el.offsetWidth + 2 * FOOT_BLEED, h: el.offsetHeight + 2 * FOOT_BLEED,
      rx: Math.max(0, Math.min(parseFloat(getComputedStyle(el).borderTopLeftRadius) || 0, el.offsetHeight / 2) - FOOT_SQUARE),
    }));
    for (let i = boxes.length; i < FOOT_MAX; i++) footRefs.current[i]?.setAttribute('width', '0');
    // Deux temps qui ne se chevauchent pas : à l'ouverture le panneau d'abord, le
    // pied ensuite ; à la fermeture le pied d'abord, le panneau ensuite.
    const [pDelay, pDur] = st.closing ? [hasFoot ? CLOSE_FOOT : 0, CLOSE_PANEL]
      : first ? [0, OPEN_PANEL] : [0, GROW];
    const [fDelay, fDur] = st.closing ? [0, CLOSE_FOOT] : [OPEN_PANEL, OPEN_FOOT];
    const phase = (ms: number, delay: number, dur: number) => Math.min(Math.max((ms - delay) / dur, 0), 1);
    let raf = 0;
    const t0 = performance.now();
    const tick = (now: number) => {
      const ms = now - t0;
      const tp = phase(ms, pDelay, pDur);
      const box = lerpBox(pFrom, pTo, ease(tp));
      setBox(el, box);
      curRef.current = box;
      const tf = phase(ms, fDelay, fDur);
      // Sur une croissance, le pied est déjà sorti : il suit la nouvelle
      // géométrie sans rejouer son apparition.
      const fe = st.closing ? ease(1 - tf) : first ? ease(tf) : 1;
      boxes.forEach((b, i) => {
        const fl = footRefs.current[i];
        if (!fl) return;
        // Tant que le pied n'est pas de sortie, il n'existe pas : sinon sa graine
        // flotterait au bas d'un panneau qui n'est pas encore arrivé.
        if (fe <= 0) fl.setAttribute('width', '0');
        else setBox(fl, lerpBox(seed(b), b, fe));
      });
      if (tp < 1 || tf < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [st, geom, panelRadius, hasFoot]);

  if (!st.shown || !geom) return null;

  /**
   * Zone de travail du filtre, en COORDONNÉES ÉCRAN : panneau ∪ bouton ∪ pied,
   * élargie de 80 px (l'ombre porte à dy 10 + blur 9 ≈ 37 px, la diffusion du
   * goo à blur 6 ≈ 18 px). Le réglage d'origine — `-25% / 150%` de la bounding
   * box — laissait WebKit recalculer cette zone PENDANT que la goutte grandit
   * (rAF, setAttribute). Sur iOS, des fois il la figeait trop petite : le
   * feDropShadow était alors tranché net en rectangle, en bas et à droite —
   * l'ombre « coupée au petit bonheur la chance » du 10/08, jamais reproduite
   * sous Chromium qui, lui, recalcule correctement. Ici la zone ne dépend plus
   * de la bbox : elle est bonne dès la première image et à chaque re-mesure.
   */
  const FP = 80;
  const fLeft = Math.min(geom.panel.x, geom.anchor.x) - FP;
  const fTop = Math.min(geom.panel.y, geom.anchor.y) - FP;
  const fRight = Math.max(geom.panel.x + geom.panel.w, geom.anchor.x + geom.anchor.w) + FP;
  const fBottom = Math.max(
    geom.panel.y + geom.panel.h + (hasFoot ? GAP + 140 : 0),
    geom.anchor.y + geom.anchor.h,
  ) + FP;

  return createPortal(
    <>
      {/* Sans voile, il faut quand même intercepter le clic extérieur pour fermer. */}
      <div onClick={onClose}
        className={`fixed inset-0 z-[2900] transition-opacity duration-200 ${
          backdrop ? 'bg-[var(--scrim)] veil-sm' : ''} ${st.closing ? 'opacity-0' : 'opacity-100'}`} />

      {/* `overflow-visible`, et c'est le morceau qui manquait : un <svg> rogne
          par défaut à sa boîte (feuille UA), et sur iOS cette boîte — mesurée
          sur innerWidth/innerHeight, entre barre d'URL et safe areas — ne
          coïncide jamais exactement avec la zone visible. L'ombre de la matière,
          qui déborde, était donc tranchée net sur les côtés, de façon stable
          (test utilisateur du 10/08 : B boxé, C plein viewport boxé aussi —
          donc pas la filter region —, E = B + overflow:visible parfait à
          chaque ouverture). Le svg est fixed et pointer-events-none : déborder
          ne crée ni scroll ni zone cliquable. */}
      <svg className="fixed inset-0 z-[2905] pointer-events-none overflow-visible"
        width={geom.W} height={geom.H} viewBox={`0 0 ${geom.W} ${geom.H}`} aria-hidden>
        <defs>
          {/* color-interpolation-filters="sRGB" : sans lui le flou travaille en
              linearRGB et la matière ressort plus claire que le bouton plat de
              même couleur — la « couche transparente » qu'on croyait voir.
              Puis blur 8 + seuil alpha dur (30/-14) : bords nets. L'ombre est
              portée par la matière elle-même, jamais par le panneau HTML. */}
          <filter id={filterId} filterUnits="userSpaceOnUse"
            x={fLeft} y={fTop} width={fRight - fLeft} height={fBottom - fTop}
            colorInterpolationFilters="sRGB">
            {/* stdDeviation 8→6 et 13→9 : le flou domine le coût de rendu logiciel
                de ce filtre sur WebKit (Safari iOS ne l'accélère pas comme
                Chromium) — allégé ici sans changer le rendu visuel du seuil dur. */}
            <feGaussianBlur in="SourceGraphic" stdDeviation="6" result="blur" />
            <feColorMatrix in="blur" mode="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 30 -14" result="goo" />
            <feDropShadow dx="0" dy="10" stdDeviation="9" floodColor="#000" floodOpacity="0.5" />
          </filter>
        </defs>
        <g filter={`url(#${filterId})`} fill={color}>
          {/* La pastille immobile qui tient le bouton pendant que le panneau décolle :
              taillée sur l'ancre, jamais plus grande qu'elle. */}
          <rect x={geom.anchor.x - 3} y={geom.anchor.y - 3} width={geom.anchor.w + 6} height={geom.anchor.h + 6} rx={geom.anchor.rx + 3} />
          <rect ref={rectRef} x={geom.anchor.x} y={geom.anchor.y} width={geom.anchor.w} height={geom.anchor.h} rx={geom.anchor.rx} />
          {footer && Array.from({ length: FOOT_MAX }, (_, i) => (
            <rect key={i} ref={el => { footRefs.current[i] = el; }} x={0} y={0} width={0} height={0} rx={3} />
          ))}
        </g>
      </svg>

      {/* Clone du bouton, au-dessus du voile : re-clic = fermer. Il reprend la taille
          ET le rayon du vrai bouton, sinon on croit en voir deux. */}
      {/* Le clone garde le RELIEF du vrai bouton (`--bump` + `--sh-fab`). On l'avait
          aplati pour qu'il fonde dans la goutte SVG, unie : la couture disparaissait,
          mais le bouton changeait d'aspect au moment précis où on appuie dessus —
          et c'est ce changement-là qui se remarque, bien plus qu'une couture. Un
          bouton ne doit pas se transformer sous le doigt. */}
      <button type="button" aria-label="Fermer" onClick={onClose}
        className="fixed z-[2950] flex items-center justify-center overflow-hidden shadow-[var(--sh-fab)] text-[var(--otto-ink)]"
        style={{ left: geom.anchor.x, top: geom.anchor.y, width: geom.anchor.w, height: geom.anchor.h,
                 borderRadius: geom.anchor.rx, background: cloneColor ?? color,
                 backgroundImage: 'var(--bump)' }}>
        {cloneIcon}
      </button>

      {/* PAS de `overflow-hidden` ici, et c'est un choix, pas un oubli.
          Le panneau rognait à son propre bord, et la matière de l’app DÉBORDE
          largement du sien : `--sh-emboss` porte sa lueur claire à `-4px -4px 9px`
          et le pseudo-élément passe encore sous un `blur(2.5px)`, soit une
          vingtaine de pixels vers le haut-gauche. Aucune marge intérieure ne
          pouvait gagner contre ce rognage — le padding déplace le contenu, pas la
          limite de découpe. La lueur ressortait donc tranchée en un liseré
          parfaitement droit le long des bords, le plus net en HAUT et sur les
          CÔTÉS puisque la lumière vient de là ; le bas, lui, était indemne
          (signalé le 10/08, deux fois). C'est ce liseré qu'on lisait comme « des
          calques empilés dont chaque ombre est encaissée ».
          Le rayon reste posé : les enfants qui peignent jusqu'au bord arrondissent
          eux-mêmes (`--goo-r`, cf. OttoChat), les autres ont leur marge. */}
      <div className={`fixed z-[2910] text-white flex flex-col ${st.closing ? 'og-goopop-out' : 'og-goopop-content'}`}
        style={{ left: geom.panel.x, top: geom.panel.y, width: geom.panel.w, height: geom.panel.h,
                 borderRadius: panelRadius + GOO_ROUND,
                 // Le rayon EFFECTIF, publié au contenu : les bandeaux collés aux
                 // bords doivent épouser la même courbe que le panneau, sinon
                 // leurs angles mordent dessus. Codé en dur côté appelant, il ne
                 // suivait pas la correction du rongement.
                 ['--goo-r' as string]: `${panelRadius + GOO_ROUND}px`,
                 // Le HTML suit la matière quand elle grandit, sur la même durée
                 // et la même courbe (easeInOutCubic) — sinon le contenu arrive
                 // à destination avant son fond.
                 transition: `left ${GROW}ms cubic-bezier(.65,0,.35,1), top ${GROW}ms cubic-bezier(.65,0,.35,1), width ${GROW}ms cubic-bezier(.65,0,.35,1), height ${GROW}ms cubic-bezier(.65,0,.35,1)` }}>
        {children}
      </div>

      {/* Pied HORS du panneau : filtres et actions posés sous la matière, sans fond
          commun — ils commandent le contenu, ils n'en font pas partie. */}
      {footer && (
        <div ref={footBoxRef}
          className={`fixed z-[2910] ${st.closing ? 'og-goopop-foot-out' : 'og-goopop-foot'}`}
          style={{ left: geom.panel.x, top: geom.panel.y + geom.panel.h + GAP, width: geom.panel.w }}>
          {footer}
        </div>
      )}

      {/* Le contenu ne paraît QU'UNE FOIS la matière arrivée, et s'efface AVANT
          qu'elle ne reparte : sinon on lit des lignes flottant hors de leur boîte. */}
      <style>{`
        @keyframes ogGooPopContent {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes ogGooPopOut {
          from { opacity: 1; transform: translateY(0); }
          to   { opacity: 0; transform: translateY(6px); }
        }
        .og-goopop-content   { animation: ogGooPopContent .17s ease-out ${OPEN_PANEL}ms both; }
        /* Le contenu s'efface D'ABORD, sans attendre : le délai calculé sur la fin
           de la fermeture le laissait à pleine opacité pendant que la matière était
           déjà repartie vers le bouton — on lisait le chat flottant dans le vide,
           puis disparaissant d'un coup. Il part avec le premier tiers du geste. */
        .og-goopop-out       { animation: ogGooPopOut .16s ease-in both; }
        .og-goopop-foot      { animation: ogGooPopContent .15s ease-out ${OPEN_PANEL + OPEN_FOOT}ms both; }
        .og-goopop-foot-out  { animation: ogGooPopOut .1s ease-in both; }
      `}</style>
    </>,
    document.body,
  );
}
